// Console instrumentation for a live class's data usage, broken into phases.
//
// What gets posted to the backend is one number for the whole class (see
// consumptionTracker.trackLiveSession) — the right thing to bill on, and useless for
// answering "what does the camera actually cost next to the mic". This logger sits on the
// same 2s networkStats samples the on-screen readout uses and cuts them at every change of
// what is being published, so a run through mic -> camera -> screen -> whiteboard ends with a
// table of what each mode cost.
//
// Phase boundaries land on sample ticks, not on the click: bytes are only known every 2s, so
// up to one tick of a phase can be attributed to the one before it. Over 30s legs that is
// noise; over 2s legs it would not be.
//
// Nothing here changes what is billed. From the console:
//   window.__consumption.summary()        print the table so far, mid-class
//   window.__consumption.verbose = false  keep the phase/summary lines, drop the per-tick ones
//   window.__consumption.enabled = false  silence entirely

const formatBytes = (bytes = 0) => {
    if (!bytes || bytes < 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB'];
    const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, power);
    return `${value.toFixed(power === 0 ? 0 : 1)} ${units[power]}`
};

const kbps = (bitsPerSecond = 0) => `${Math.round((bitsPerSecond || 0) / 1000)} kbps`;

const clock = (ms) => {
    const total = Math.max(Math.round(ms / 1000), 0);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
};

const average = (values) => (values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null);

// The label is the whole point of the log, so it is built from what the user did rather than
// from producer kinds: camera+mic, screen+mic and whiteboard+mic are all "audio and video" to
// WebRTC and cost very different amounts.
const describe = ({live, audioOnly, micOn, cameraOn, screenOn, whiteboardOn} = {}) => {
    if (!live) return 'Joined (not publishing)'
    const video = whiteboardOn ? 'Whiteboard' : screenOn ? 'Screen' : cameraOn ? 'Camera' : null;
    // The whiteboard rides on a screen share, so a camera on top of it is the inset self-view.
    const extra = whiteboardOn && cameraOn ? ' + camera' : '';
    if (!video) return audioOnly || micOn ? 'Mic only' : 'Live (no media)'
    return `${video}${extra}${micOn ? ' + mic' : ' (muted)'}`
};

class ConsumptionPhaseLogger {
    constructor() {
        if (ConsumptionPhaseLogger._instance) return ConsumptionPhaseLogger._instance
        ConsumptionPhaseLogger._instance = this;
        this.enabled = true;
        this.verbose = true;
        this.reset();
    }

    reset() {
        this.startedAt = 0;
        this.streamId = null;
        this.phases = [];
        this.current = null;
        this.lastSample = null;
        this.mode = {};
        // userId -> {audio, video}: newest producerScore seen. Own scores drive the phase
        // stats; everyone else's are kept so a bad phase can be pinned on the right person.
        this.scores = {};
        this.selfId = null;
    }

    start({selfId, streamId} = {}) {
        if (!this.enabled) return
        this.reset();
        this.startedAt = Date.now();
        this.selfId = selfId != null ? `${selfId}` : null;
        this.streamId = streamId;
        console.log(`%c[consumption] session started — stream ${streamId}, you are ${this.selfId}`,
            'color:#6c5ce7;font-weight:bold');
    }

    // Called whenever the publish state changes. A repeated label is a no-op, so this is safe
    // to fire from a render effect.
    setMode(mode = {}) {
        if (!this.enabled) return
        this.mode = mode;
        const label = describe(mode);
        if (this.current && this.current.label === label) return
        this._closeCurrent();
        this.current = {
            label,
            startedAt: Date.now(),
            elapsedAtStart: this.startedAt ? Date.now() - this.startedAt : 0,
            sent: 0,
            received: 0,
            sentAudio: 0,
            sentVideo: 0,
            recvAudio: 0,
            recvVideo: 0,
            upSamples: [],
            downSamples: [],
            rttSamples: [],
            lossSamples: [],
            audioScores: [],
            videoScores: [],
            ticks: 0,
        };
        // The server only pushes producerScore when the score *changes*, so a steady 30s leg
        // can pass without a single event and the phase would report no score at all. Seed it
        // with what is already known — but only for a kind this phase actually publishes,
        // otherwise the camera's last score would follow it into a mic-only phase.
        const mine = this.scores[this.selfId] || {};
        const hasVideo = !!(mode.cameraOn || mode.screenOn || mode.whiteboardOn);
        const hasAudio = !!(mode.micOn || mode.audioOnly);
        if (mode.live && hasAudio && Number.isFinite(mine.audio)) this.current.audioScores.push(mine.audio);
        if (mode.live && hasVideo && Number.isFinite(mine.video)) this.current.videoScores.push(mine.video);
        console.log(`%c[consumption] ---- phase ${this.phases.length + 1}: ${label} — starts at ${clock(this.current.elapsedAtStart)}`,
            'color:#00b894;font-weight:bold');
    }

    // mediasoup's own view of the uplink, 0-10, pushed per producer whenever the server
    // re-scores it. Kept per phase so the table can say "the camera leg cost 4 MB and scored
    // 7" — a cheap phase with a low score is a phase the encoder throttled, not a cheap mode.
    recordScore({userId, kind, score} = {}) {
        if (!this.enabled || !userId) return
        const id = `${userId}`;
        this.scores[id] = {...(this.scores[id] || {}), [kind]: score};
        if (id !== this.selfId || !this.current || !Number.isFinite(score)) return
        if (kind === 'audio') this.current.audioScores.push(score);
        else this.current.videoScores.push(score);
        if (this.verbose){
            console.log(`%c[consumption]   producerScore ${kind} = ${score}/10 (${this.current.label})`,
                score >= 8 ? 'color:#00b894' : score >= 5 ? 'color:#fdcb6e' : 'color:#d63031');
        }
    }

    // One 2s networkStats sample. The payload carries cumulative counters, so every per-phase
    // figure is a difference between consecutive samples rather than an absolute.
    tick(sample) {
        if (!this.enabled || !sample) return
        const previous = this.lastSample;
        this.lastSample = sample;
        if (!previous) return
        if (!this.current) this.setMode(this.mode);

        const since = (key) => Math.max((sample[key] || 0) - (previous[key] || 0), 0);
        const sent = since('bytesSent');
        const received = since('bytesReceived');
        const phase = this.current;
        phase.sent += sent;
        phase.received += received;
        phase.sentAudio += since('sentAudioBytes');
        phase.sentVideo += since('sentVideoBytes');
        phase.recvAudio += since('recvAudioBytes');
        phase.recvVideo += since('recvVideoBytes');
        phase.ticks += 1;
        phase.upSamples.push(sample.upBps || 0);
        phase.downSamples.push(sample.downBps || 0);
        if (Number.isFinite(sample.rtt)) phase.rttSamples.push(sample.rtt * 1000);
        if (Number.isFinite(sample.lossPct)) phase.lossSamples.push(sample.lossPct);

        if (!this.verbose) return
        const mine = this.scores[this.selfId] || {};
        const scoreText = `a:${Number.isFinite(mine.audio) ? mine.audio : '-'} v:${Number.isFinite(mine.video) ? mine.video : '-'}`;
        console.log(
            `[consumption] ${clock(Date.now() - this.startedAt)} ${phase.label.padEnd(22)}`
            + ` up ${kbps(sample.upBps).padStart(10)}  down ${kbps(sample.downBps).padStart(10)}`
            + ` | this tick ${formatBytes(sent).padStart(9)} up / ${formatBytes(received).padStart(9)} down`
            + ` | phase ${formatBytes(phase.sent)} up / ${formatBytes(phase.received)} down`
            + ` | score ${scoreText}`
            + ` | rtt ${Number.isFinite(sample.rtt) ? `${Math.round(sample.rtt * 1000)}ms` : '-'}`
            + ` loss ${(sample.lossPct || 0).toFixed(1)}%`
        );
    }

    _closeCurrent() {
        if (!this.current) return
        const phase = this.current;
        phase.endedAt = Date.now();
        phase.durationMs = phase.endedAt - phase.startedAt;
        this.phases.push(phase);
        this.current = null;
        console.log(`%c[consumption] ---- end ${phase.label}: ${clock(phase.durationMs)}, `
            + `${formatBytes(phase.sent)} up / ${formatBytes(phase.received)} down `
            + `(${formatBytes(phase.sent + phase.received)} total)`,
            'color:#0984e3');
    }

    _row(phase, index) {
        const seconds = Math.max((phase.durationMs ?? (Date.now() - phase.startedAt)) / 1000, 0.001);
        const videoScore = average(phase.videoScores);
        const audioScore = average(phase.audioScores);
        return {
            '#': index + 1,
            phase: phase.label,
            'started at': clock(phase.elapsedAtStart),
            duration: clock(seconds * 1000),
            up: formatBytes(phase.sent),
            down: formatBytes(phase.received),
            total: formatBytes(phase.sent + phase.received),
            'up audio': formatBytes(phase.sentAudio),
            'up video': formatBytes(phase.sentVideo),
            'avg up': kbps(average(phase.upSamples) || 0),
            'avg down': kbps(average(phase.downSamples) || 0),
            'MB/hour': (((phase.sent + phase.received) / seconds) * 3600 / (1024 * 1024)).toFixed(1),
            'score audio': audioScore === null ? '-' : audioScore.toFixed(1),
            'score video': videoScore === null ? '-' : videoScore.toFixed(1),
            'worst video': phase.videoScores.length ? Math.min(...phase.videoScores) : '-',
            'avg rtt': phase.rttSamples.length ? `${Math.round(average(phase.rttSamples))}ms` : '-',
            'peak loss': phase.lossSamples.length ? `${Math.max(...phase.lossSamples).toFixed(1)}%` : '-',
        }
    }

    // Prints the table without ending the session, for reading mid-class from the console.
    // The phase still running is included, marked open, so a leg you are in the middle of is
    // not invisible.
    summary() {
        const phases = this.current ? [...this.phases, this.current] : this.phases;
        if (!phases.length){
            console.log('[consumption] nothing recorded yet');
            return []
        }
        const rows = phases.map((phase, index) => {
            const row = this._row(phase, index);
            if (phase === this.current) row.phase = `${row.phase} (open)`;
            return row
        });
        console.table(rows);
        return rows
    }

    finish() {
        if (!this.enabled) return []
        this._closeCurrent();
        const sample = this.lastSample || {};
        const totalSent = sample.bytesSent || 0;
        const totalReceived = sample.bytesReceived || 0;
        const durationMs = this.startedAt ? Date.now() - this.startedAt : 0;
        console.log(`%c[consumption] session ended — ${clock(durationMs)}, `
            + `${formatBytes(totalSent)} up / ${formatBytes(totalReceived)} down, `
            + `${formatBytes(totalSent + totalReceived)} total`,
            'color:#6c5ce7;font-weight:bold');
        const rows = this.phases.length ? this.summary() : [];
        console.log('[consumption] session split by kind:',
            `up audio ${formatBytes(sample.sentAudioBytes || 0)},`,
            `up video ${formatBytes(sample.sentVideoBytes || 0)},`,
            `down audio ${formatBytes(sample.recvAudioBytes || 0)},`,
            `down video ${formatBytes(sample.recvVideoBytes || 0)}`);
        return rows
    }
}

const consumptionPhaseLogger = new ConsumptionPhaseLogger();

// Console handle: window.__consumption.summary() any time, or flip .verbose / .enabled.
if (typeof window !== 'undefined') window.__consumption = consumptionPhaseLogger;

export default consumptionPhaseLogger;
